<template>
  <div class="app-layout-navigation">
    <nav class="app-layout-navigation__trail">
      <v-breadcrumbs :items="breadcrumbItems" density="compact" />
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import NavigationRoutes from '../sidebar/NavigationRoutes'
import { isSettingsShippingBranch, isShippingCostNavigationRoute } from '../../utils/shippingCostNavigation'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

type BreadcrumbNavigationItem = {
  label: string
  to: string
  hasChildren: boolean
}

const integrationPlatformLabels: Record<string, string> = {
  google: 'Google Ads',
  facebook: 'Facebook',
  microsoft: 'Microsoft Ads',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
  snapchat: 'Snapchat',
}

const findRouteName = (name: string) => {
  const traverse = (routers: any[]): string => {
    for (const routerItem of routers) {
      if (routerItem.name === name) {
        return routerItem.displayName
      }
      if (routerItem.children) {
        const result = traverse(routerItem.children)
        if (result) {
          return result
        }
      }
    }
    return ''
  }

  return traverse(NavigationRoutes.routes)
}

const items = computed(() => {
  const result: BreadcrumbNavigationItem[] = []

  if (route.name === 'marketing-channel' || route.name === 'marketing-channel-manage') {
    result.push({
      label: 'Integrations',
      to: '/integrations/marketing-channel',
      hasChildren: false,
    })

    if (route.name === 'marketing-channel-manage' && route.params.platformId) {
      const pid = String(route.params.platformId)
      const platformName =
        integrationPlatformLabels[pid] ||
        pid
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      result.push({
        label: `${platformName} Accounts`,
        to: route.path,
        hasChildren: false,
      })
    }
    return result
  }

  if (route.name === 'dropship-pod' || route.name === 'dropship-pod-connect') {
    result.push({
      label: t('menu.dropship-pod'),
      to: '/integrations/dropship-pod',
      hasChildren: false,
    })
    if (route.name === 'dropship-pod-connect') {
      result.push({
        label: 'Connect provider',
        to: route.path,
        hasChildren: false,
      })
    }
    return result
  }

  if (isShippingCostNavigationRoute(route.name, route.path, route.matched)) {
    const costSettingsLabel = findRouteName('cost-settings')
    if (costSettingsLabel) {
      result.push({
        label: t(costSettingsLabel),
        to: '/cost-settings/product-cost',
        hasChildren: true,
      })
    }

    const shippingListPath = isSettingsShippingBranch(route.path)
      ? router.resolve({ name: 'settings-shipping', query: { tab: 'zones' } }).path
      : router.resolve({ name: 'shipping-cost', query: { tab: 'zones' } }).path

    const isShippingListRoute = route.name === 'shipping-cost' || route.name === 'settings-shipping'
    result.push({
      label: t('menu.shipping-cost'),
      to: shippingListPath,
      hasChildren: !isShippingListRoute,
    })

    if (!isShippingListRoute) {
      if (route.name === 'shipping-cost-zone-new' || route.name === 'settings-shipping-zone-new') {
        result.push({ label: 'New zone', to: route.path, hasChildren: false })
      } else if (route.name === 'shipping-cost-zone-edit' || route.name === 'settings-shipping-zone-edit') {
        result.push({ label: 'Edit zone', to: route.path, hasChildren: false })
      } else if (route.name === 'shipping-profile-edit' || route.name === 'settings-shipping-profile-edit') {
        result.push({ label: 'Edit profile', to: route.path, hasChildren: false })
      }
    }

    return result
  }

  route.matched.forEach((matchedRoute) => {
    const labelKey = findRouteName(matchedRoute.name as string)
    if (!labelKey) {
      return
    }
    result.push({
      label: t(labelKey),
      to: matchedRoute.path,
      hasChildren: matchedRoute.children && matchedRoute.children.length > 0,
    })
  })
  return result
})

const breadcrumbItems = computed(() => {
  const home = {
    title: 'Home',
    disabled: false,
    to: router.resolve({ name: 'dashboard' }).path,
  }

  const trail = items.value.map((item) => ({
    title: item.label,
    disabled: item.hasChildren,
    onClick: item.hasChildren ? undefined : () => router.push(item.to),
  }))

  return [home, ...trail]
})
</script>

<style lang="scss" scoped>
.app-layout-navigation__trail {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  overflow-x: auto;
}

:deep(.v-breadcrumbs) {
  flex-wrap: nowrap;
  padding-inline: 0;
}

:deep(.v-breadcrumbs-item) {
  color: rgb(var(--v-theme-text-secondary));
  font-size: 0.875rem;
  cursor: pointer;
}

:deep(.v-breadcrumbs-item--disabled) {
  color: rgb(var(--v-theme-text-primary));
  font-weight: 500;
  opacity: 1;
}

:deep(.v-breadcrumbs-divider) {
  color: rgb(var(--v-theme-text-secondary));
}
</style>
